/**
 * Mobile-only horizontal album carousel with iPod-style cover flow.
 * Uses Framer Motion drag for swipe interaction. The selected album
 * is centered and full-size; neighbors are scaled down and faded.
 */
import { useRef, useEffect, useState, useCallback } from "react";
import { motion, useMotionValue, useSpring, animate } from "framer-motion";
import BlurImage from "../BlurImage";
import TiltCard from "./TiltCard";
import type { VinylAlbum } from "@/hooks/useVinylPlayer";

interface AlbumCarouselProps {
  albums: VinylAlbum[];
  currentAlbumId: string | null;
  vinylPosition: string;
  onSelectAlbum: (album: VinylAlbum) => void;
}

const ITEM_WIDTH = 180;
const ITEM_GAP = 12;
const ITEM_TOTAL = ITEM_WIDTH + ITEM_GAP;

const AlbumCarousel = ({ albums, currentAlbumId, vinylPosition, onSelectAlbum }: AlbumCarouselProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 300, damping: 30 });
  const [containerWidth, setContainerWidth] = useState(0);

  // Measure container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let raf = 0;
    const measure = () => {
      const w = el.offsetWidth;
      setContainerWidth((prev) => (prev === w ? prev : w));
    };
    measure();
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  // Snap to selected album
  const snapTo = useCallback((index: number) => {
    if (!containerWidth) return;
    const center = containerWidth / 2 - ITEM_WIDTH / 2;
    const target = center - index * ITEM_TOTAL;
    animate(x, target, { type: "spring", stiffness: 300, damping: 30 });
  }, [containerWidth, x]);

  // Snap when selection changes
  useEffect(() => {
    const idx = albums.findIndex(a => a.id === currentAlbumId);
    if (idx >= 0) snapTo(idx);
  }, [currentAlbumId, albums, snapTo]);

  // Initial position
  useEffect(() => {
    if (!containerWidth) return;
    const idx = Math.max(0, albums.findIndex(a => a.id === currentAlbumId));
    const center = containerWidth / 2 - ITEM_WIDTH / 2;
    x.set(center - idx * ITEM_TOTAL);
  }, [containerWidth]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDragEnd = () => {
    if (!containerWidth) return;
    const center = containerWidth / 2;
    const currentX = x.get();
    
    // Find nearest album to center
    let nearestIdx = 0;
    let nearestDist = Infinity;
    albums.forEach((_, i) => {
      const itemCenter = currentX + i * ITEM_TOTAL + ITEM_WIDTH / 2;
      const dist = Math.abs(itemCenter - center);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearestIdx = i;
      }
    });

    snapTo(nearestIdx);
    if (albums[nearestIdx].id !== currentAlbumId) {
      onSelectAlbum(albums[nearestIdx]);
    }
  };

  const totalWidth = albums.length * ITEM_TOTAL - ITEM_GAP;
  const dragLeft = -(totalWidth - containerWidth + containerWidth / 2 - ITEM_WIDTH / 2);
  const dragRight = containerWidth / 2 - ITEM_WIDTH / 2;

  return (
    <div ref={containerRef} className="relative w-full overflow-hidden" style={{ height: ITEM_WIDTH + 40 }}>
      <motion.div
        className="flex absolute top-0 left-0"
        style={{ x: springX, gap: ITEM_GAP }}
        drag="x"
        dragConstraints={{ left: Math.min(dragLeft, dragRight), right: Math.max(dragLeft, dragRight) }}
        dragElastic={0.15}
        onDragEnd={handleDragEnd}
      >
        {albums.map((album) => {
          const isSelected = currentAlbumId === album.id;
          const isThisVinylOut = currentAlbumId === album.id && vinylPosition !== 'sleeve';

          return (
            <motion.div
              key={album.id}
              className="flex-shrink-0 cursor-pointer"
              style={{ width: ITEM_WIDTH }}
              animate={{
                scale: isSelected ? 1 : 0.82,
                opacity: isSelected ? 1 : 0.55,
              }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onTap={() => onSelectAlbum(album)}
            >
              <TiltCard
                className="aspect-square w-full"
                ariaLabel={`Select ${album.title}`}
                maxTilt={8}
                vinylPeekOnHover
                isVinylOut={isThisVinylOut}
              >
                <BlurImage
                  src={album.cover}
                  alt={album.title}
                  className="h-full w-full object-cover rounded-sm"
                  loading="lazy"
                />
                {isSelected && (
                  <div className="absolute inset-0 ring-2 ring-foreground rounded-sm" />
                )}
              </TiltCard>
              <p className="text-center text-xs text-muted-foreground mt-2 truncate px-1">
                {album.title}
              </p>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default AlbumCarousel;
